// Shop short names (docs/SHOP-SHORT-URLS.md): the readable part of a shop's hub address,
// hub.popcartph.com/shop/<slug> and /ops/shops/<slug>. One set of rules for the API (which owns
// uniqueness and history) and the hub (which previews and pre-checks as you type).
//
//   * 3 to 40 characters of a–z and 0–9, single hyphens between words: aling-nena, 7-eleven-qc
//   * never a reserved word (routes and words people would trust), never shaped like an id
//   * made once from the shop name; renaming the shop keeps it. Ops may change it on purpose,
//     and the old one keeps redirecting forever (and is never given to another shop).

export const SLUG_MIN = 3;
export const SLUG_MAX = 40;
export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Words a shop can't take: hub and site routes, and names people would mistake for PopCart itself. */
export const RESERVED_SLUGS = Object.freeze([
  'admin', 'api', 'app', 'apps', 'assets', 'auth', 'billing', 'checkout', 'delete-account', 'edit', 'help', 'hub',
  'login', 'logout', 'me', 'new', 'null', 'official', 'ops', 'orders', 'partner', 'partners', 'pay', 'payments',
  'popcart', 'popcart-ph', 'popcartph', 'privacy', 'rider', 'riders', 'security', 'settings', 'shop', 'shops',
  'sign-in', 'sign-out', 'signin', 'signup', 'static', 'support', 'terms', 'undefined', 'www', 'ws',
]);

/** True for a UUID (a shop id), so a ref can be told apart from a short name. */
export function isShopId(ref) {
  return typeof ref === 'string' && UUID_RE.test(ref);
}

/** The short name a shop name suggests (unchecked for uniqueness; the API adds -2, -3 … on a clash). */
export function suggestShopSlug(name) {
  const s = String(name ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/ñ/g, 'n')
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  let out = s.length > SLUG_MAX ? s.slice(0, SLUG_MAX).replace(/-[^-]*$/, '') || s.slice(0, SLUG_MAX) : s;
  out = out.replace(/-+$/, '');
  if (out.length < SLUG_MIN) out = out ? `${out}-shop` : 'unnamed-shop';
  if (RESERVED_SLUGS.includes(out)) out = `${out}-shop`;
  return out;
}

/** Tidy what someone typed into the address box: lower case, spaces and _ become hyphens. */
export function tidyShopSlug(raw) {
  return String(raw ?? '').trim().toLowerCase().replace(/[\s_]+/g, '-').replace(/-{2,}/g, '-');
}

/** Why a short name can't be used, in plain words, or null when it's fine (uniqueness aside). */
export function shopSlugProblem(slug) {
  if (typeof slug !== 'string' || !slug) return 'Give the shop a web address.';
  if (slug.length < SLUG_MIN) return `At least ${SLUG_MIN} characters.`;
  if (slug.length > SLUG_MAX) return `${SLUG_MAX} characters at most.`;
  if (!SLUG_RE.test(slug)) return 'Use a–z, 0–9 and single hyphens between words, like aling-nena.';
  if (isShopId(slug)) return 'That looks like an id. Use words, like aling-nena.';
  if (RESERVED_SLUGS.includes(slug)) return 'That word is reserved. Try the shop’s name.';
  return null;
}
